import { TanStackDevtools } from "@tanstack/react-devtools";
import { formDevtoolsPlugin } from "@tanstack/react-form-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { initAnalytics, initDatadogRumEarly } from "./lib/analytics-loader";
import { auth } from "./lib/auth";
import { queryClient } from "./lib/query";
import { routeTree } from "./lib/routeTree.gen";
import { registerServiceWorker } from "./lib/service-worker";
import "./styles/globals.css";

const router = createRouter({
  routeTree,
  context: {
    auth,
    queryClient,
  },
});

// Initialize Datadog RUM before first render so its fetch monkey-patch
// injects trace headers on the very first tRPC request.
await initDatadogRumEarly();

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {import.meta.env.DEV && (
        <TanStackDevtools
          config={{
            position: "bottom-right",
            hideUntilHover: true,
          }}
          plugins={[
            {
              name: "TanStack Query",
              render: <ReactQueryDevtoolsPanel />,
              defaultOpen: true,
            },
            {
              name: "TanStack Router",
              render: <TanStackRouterDevtoolsPanel router={router} />,
              defaultOpen: false,
            },
            formDevtoolsPlugin(),
          ]}
        />
      )}
    </QueryClientProvider>
  </StrictMode>,
);

// Initialize analytics after render (deferred loading)
initAnalytics(router);

// Register service worker for push notifications (not on the portfolio host)
registerServiceWorker();

if (import.meta.hot) {
  import.meta.hot.dispose(() => root.unmount());
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
